import { FileDown, ArrowLeftRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SbarHandoffPanel — One-click deterministic SBAR hand-off generator.
 *
 * Built 2026-06-08 (founder GO). Opens
 * `GET /api/encounter/{encounterId}/sbar` in a new tab; the backend
 * returns a hash-chained HTML document per encounter that the receiving
 * clinician can print or save to PDF at shift change.
 *
 * Architectural notes:
 *  - SBAR is the WHO Patient Safety Solution #3 / Joint Commission
 *    hand-off communication framework.
 *  - This component is pure dispatch — all clinical content rendering
 *    happens server-side in sbar_generator.py. Zero clinical knowledge
 *    lives in this React file.
 *  - No engine-name strings appear here either, by design: the panel
 *    surfaces the same button regardless of which engines fired.
 */
export default function SbarHandoffPanel({ encounterId }) {
  if (!encounterId) return null;

  const openSbar = () => {
    const url = `${API}/api/encounter/${encounterId}/sbar`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      className="bg-white rounded-lg border-2 border-slate-200 p-4"
      data-testid="sbar-handoff-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <ArrowLeftRight className="w-4 h-4 text-slate-700" />
        <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
          SBAR Hand-off (shift change)
        </h2>
      </div>
      <p className="text-[11px] text-slate-600 leading-relaxed mb-3">
        Deterministic Situation / Background / Assessment / Recommendation
        document for shift hand-off. WHO Patient Safety Solution&nbsp;#3.
        Hash-chained per encounter. No LLM. Same encounter &rarr; same SBAR.
      </p>
      <button
        type="button"
        onClick={openSbar}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        data-testid="sbar-download-button"
      >
        <FileDown className="w-4 h-4" />
        Open SBAR Hand-off
      </button>
    </section>
  );
}
